/**
 * LunaPin's own mark, drawn inline where a surface needs the icon rather than the favicon.
 *
 * ── Why this exists at all ───────────────────────────────────────────────────
 *
 * The bookmarklet in the settings window used to read `📌 Save to LunaPin` — a pushpin
 * emoji, which is not the product's icon and does not resemble it. LunaPin's mark is a
 * luggage tag sharpened to a point with its eyelet doubling as a pin hole, in citron on a
 * dark plate; `icons/icon.svg` is the original and states the construction. An emoji beside
 * the product's name is a placeholder that reads as a decision, and this is the one place a
 * person is asked to put LunaPin's name on their own bookmarks bar.
 *
 * ── Inline rather than `<img src="/icon.svg">` ───────────────────────────────
 *
 * The file is served — it is this page's favicon — so an `<img>` would work and would be
 * shorter. It is inline because this mark is dragged: an anchor being dropped onto the
 * bookmarks bar is serialised by the browser as it stands, and a drag preview that has to
 * fetch a second resource is a drag preview that is sometimes empty. Inline it is one
 * element with nothing to wait for.
 *
 * ── The geometry is copied, and copied deliberately ──────────────────────────
 *
 * Every path here is `icons/icon.svg`'s, which is the ORIGINAL that
 * `scripts/make-icons.sh` generates the rest from. This is a hand copy rather than a
 * generated one, so it is the exception to that rule and worth saying out loud: if the mark
 * changes, this file has to be changed with it. It is here rather than in the generator
 * because the generator writes image files and this is Kotlin the bundle compiles.
 */
package se.soderbjorn.lunapin.web

import org.w3c.dom.HTMLElement

/**
 * The mark, sized to [size] CSS pixels.
 *
 * @param gradientId the `<linearGradient>`'s id, which **must be unique in the document**:
 *   SVG ids share the page's namespace, and two copies of this mark with the same id would
 *   leave the second one's stroke referring to the first one's gradient — which works right
 *   up until the first is removed and the second turns black.
 */
internal fun lunaPinMark(size: Int = 16, gradientId: String = "lp-mark"): HTMLElement {
    val host = element("span", "lp-mark")
    // The one `innerHTML` in the bundle, and the reason `Dom.kt` says never to use it does
    // not apply: every character below is a literal in this file. Nothing here came from the
    // server, from a URL or from anything a person typed.
    host.innerHTML = """
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="$size" height="$size"
             role="img" aria-label="LunaPin">
          <defs>
            <linearGradient id="$gradientId" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#dcef7e"/>
              <stop offset="1" stop-color="#c8e04a"/>
            </linearGradient>
          </defs>
          <rect width="32" height="32" rx="8" fill="#0d080e"/>
          <rect x="0.6" y="0.6" width="30.8" height="30.8" rx="7.4" fill="none"
                stroke="#c8e04a" stroke-opacity="0.34"/>
          <g transform="translate(4 4)" fill="none" stroke="url(#$gradientId)" stroke-width="1.6"
             stroke-linecap="round" stroke-linejoin="round">
            <path d="M5.2 5.2 a2.2 2.2 0 0 1 2.2-2.2 h9.2 a2.2 2.2 0 0 1 2.2 2.2 v8.9 a2 2 0 0 1 -0.5 1.33 L12 21.6 l-6.3 -7.18 a2 2 0 0 1 -0.5 -1.33 z"/>
            <circle cx="12" cy="7.7" r="1.9"/>
          </g>
        </svg>
    """.trimIndent()
    return host
}
